import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

export interface ServerTableColumn<T> {
    key: string;
    label: string;
    width?: string;
    sortable?: boolean;
    render?: (item: T, key: string) => React.ReactNode;
}

export interface DataTableProps<T> {
    // Data
    data: T[];
    totalItems: number;
    isLoading: boolean;

    // Pagination
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;

    // Sorting
    sortColumn: string;
    sortDirection: "asc" | "desc";
    onSortChange: (column: string, direction: "asc" | "desc") => void;

    // Search
    searchTerm: string;
    onSearchChange: (term: string) => void;
    searchPlaceholder?: string;

    // Columns
    columns: ServerTableColumn<T>[];

    // Row actions
    onRowClick?: (item: T) => void;
    getRowKey: (item: T) => string;

    // Optional features
    showSearch?: boolean;
    emptyMessage?: string;
}

export const DataTable = <T,>({
    data,
    totalItems,
    isLoading,
    currentPage,
    pageSize,
    onPageChange,
    sortColumn,
    sortDirection,
    onSortChange,
    searchTerm,
    onSearchChange,
    searchPlaceholder = "Search...",
    columns,
    onRowClick,
    getRowKey,
    showSearch = true,
    emptyMessage = "No results found.",
}: DataTableProps<T>) => {
    const totalPages = Math.ceil(totalItems / pageSize);

    const getSortIcon = (columnKey: string) => {
        if (sortColumn !== columnKey) return null;
        return sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4" />
        ) : (
            <ChevronDown className="h-4 w-4" />
        );
    };

    const handleSort = (columnKey: string) => {
        if (!columns.find(col => col.key === columnKey)?.sortable) return;

        const newDirection =
            sortColumn === columnKey && sortDirection === "asc"
                ? "desc"
                : "asc";
        onSortChange(columnKey, newDirection);
    };

    const renderCell = (item: T, column: ServerTableColumn<T>) => {
        if (column.render) {
            return column.render(item, column.key);
        }

        // Default rendering - you might want to customize this
        const value = (item as any)[column.key];
        return value ?? "-";
    };

    const generatePaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink
                            href="#"
                            isActive={currentPage === i}
                            onClick={e => {
                                e.preventDefault();
                                onPageChange(i);
                            }}
                        >
                            {i}
                        </PaginationLink>
                    </PaginationItem>,
                );
            }
        } else {
            // Show first page
            items.push(
                <PaginationItem key={1}>
                    <PaginationLink
                        href="#"
                        isActive={currentPage === 1}
                        onClick={e => {
                            e.preventDefault();
                            onPageChange(1);
                        }}
                    >
                        1
                    </PaginationLink>
                </PaginationItem>,
            );

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                endPage = Math.min(totalPages - 1, 4);
            }
            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 3);
            }

            if (startPage > 2) {
                items.push(
                    <PaginationItem key="ellipsis-start">
                        <PaginationEllipsis />
                    </PaginationItem>,
                );
            }

            for (let i = startPage; i <= endPage; i++) {
                items.push(
                    <PaginationItem key={i}>
                        <PaginationLink
                            href="#"
                            isActive={currentPage === i}
                            onClick={e => {
                                e.preventDefault();
                                onPageChange(i);
                            }}
                        >
                            {i}
                        </PaginationLink>
                    </PaginationItem>,
                );
            }

            if (endPage < totalPages - 1) {
                items.push(
                    <PaginationItem key="ellipsis-end">
                        <PaginationEllipsis />
                    </PaginationItem>,
                );
            }

            if (totalPages > 1) {
                items.push(
                    <PaginationItem key={totalPages}>
                        <PaginationLink
                            href="#"
                            isActive={currentPage === totalPages}
                            onClick={e => {
                                e.preventDefault();
                                onPageChange(totalPages);
                            }}
                        >
                            {totalPages}
                        </PaginationLink>
                    </PaginationItem>,
                );
            }
        }

        return items;
    };

    return (
        <div className="space-y-4">
            {/* Search */}
            {showSearch && (
                <div className="flex items-center space-x-2">
                    <Search className="text-admin-card-foreground h-4 w-4" />
                    <Input
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={e => onSearchChange(e.target.value)}
                        className="text-admin-card-foreground max-w-sm"
                    />
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map(column => (
                                <TableHead
                                    key={column.key}
                                    className={column.width}
                                >
                                    <div className="flex items-center space-x-1">
                                        <span className="text-admin-card-foreground whitespace-nowrap text-xs">
                                            {column.label}
                                        </span>
                                        {column.sortable && (
                                            <button
                                                onClick={() =>
                                                    handleSort(column.key)
                                                }
                                                className="text-admin-card-foreground hover:text-admin-card-foreground ml-1"
                                            >
                                                {getSortIcon(column.key)}
                                            </button>
                                        )}
                                    </div>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: pageSize }).map((_, i) => (
                                <TableRow key={i}>
                                    {columns.map((col, j) => (
                                        <TableCell key={j}>
                                            <div className="bg-admin-muted h-4 w-20 animate-pulse rounded" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : data.length > 0 ? (
                            data.map(item => (
                                <TableRow
                                    key={getRowKey(item)}
                                    onClick={() => onRowClick?.(item)}
                                    className={
                                        onRowClick ? "cursor-pointer" : ""
                                    }
                                >
                                    {columns.map(column => (
                                        <TableCell
                                            key={column.key}
                                            className="text-admin-card-foreground text-xs"
                                        >
                                            {renderCell(item, column)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="text-admin-card-foreground py-8 text-center text-xs"
                                >
                                    {emptyMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-end">
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    href="#"
                                    onClick={e => {
                                        e.preventDefault();
                                        if (currentPage > 1) {
                                            onPageChange(currentPage - 1);
                                        }
                                    }}
                                    aria-disabled={currentPage <= 1}
                                />
                            </PaginationItem>
                            {generatePaginationItems()}
                            <PaginationItem>
                                <PaginationNext
                                    href="#"
                                    onClick={e => {
                                        e.preventDefault();
                                        if (currentPage < totalPages) {
                                            onPageChange(currentPage + 1);
                                        }
                                    }}
                                    aria-disabled={currentPage >= totalPages}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};
